/**
 * Displays GEL storefront prices in USD for visitors outside Georgia.
 * Checkout and Shopify's underlying price data intentionally remain in GEL.
 */
(function fitroomCurrencyDisplay() {
  const RATE_URL = 'https://api.frankfurter.dev/v2/rate/GEL/USD';
  const CACHE_KEY = 'fitroom:gel-usd-rate:v1';
  const CACHE_TTL = 12 * 60 * 60 * 1000;
  const GEL_PRICE_PATTERN = /(?:GEL|₾)\s*(-?[\d\s,.'’]+)|(-?[\d\s,.'’]+)\s*(?:GEL|₾)/gi;
  const USD_FORMATTER = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'symbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  let rate = 0;
  const scheduledRoots = new Set();
  let conversionPending = false;

  function parseAmount(value) {
    const normalized = value.replace(/[\s'’]/g, '');
    const separatorIndex = Math.max(normalized.lastIndexOf('.'), normalized.lastIndexOf(','));

    if (separatorIndex === -1) return Number(normalized);

    const decimalLength = normalized.length - separatorIndex - 1;
    if (decimalLength > 0 && decimalLength <= 2) {
      const whole = normalized.slice(0, separatorIndex).replace(/[.,]/g, '');
      const fraction = normalized.slice(separatorIndex + 1);
      return Number(`${whole}.${fraction}`);
    }

    return Number(normalized.replace(/[.,]/g, ''));
  }

  function formatUsd(gelAmount) {
    return USD_FORMATTER.format(gelAmount * rate);
  }

  function convertTextNode(node) {
    if (!node.nodeValue || !/(?:GEL|₾)/i.test(node.nodeValue) || !/\d/.test(node.nodeValue)) return;

    GEL_PRICE_PATTERN.lastIndex = 0;
    const converted = node.nodeValue.replace(GEL_PRICE_PATTERN, (match, prefixedAmount, suffixedAmount) => {
      const amount = parseAmount(prefixedAmount || suffixedAmount || '');
      return Number.isFinite(amount) ? formatUsd(amount) : match;
    });

    if (converted !== node.nodeValue) node.nodeValue = converted;
  }

  function convertText(root) {
    if (!(root instanceof Node)) return;

    if (root.nodeType === Node.TEXT_NODE) {
      convertTextNode(root);
      return;
    }

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const element = node.parentElement;
        if (!element || ['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'OPTION'].includes(element.tagName)) {
          return NodeFilter.FILTER_REJECT;
        }

        return /(?:GEL|₾)/i.test(node.nodeValue || '') && /\d/.test(node.nodeValue || '')
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      },
    });

    let node = walker.nextNode();
    while (node) {
      convertTextNode(node);
      node = walker.nextNode();
    }
  }

  function convertFilterInput(input) {
    if (!(input instanceof HTMLInputElement) || input.dataset.fitroomUsd === 'true') return;

    input.dataset.fitroomUsd = 'true';

    if (input.value) {
      const amount = parseAmount(input.value);
      if (Number.isFinite(amount)) input.value = (amount * rate).toFixed(2);
    }

    if (input.placeholder && input.placeholder !== '0') {
      const amount = parseAmount(input.placeholder);
      if (Number.isFinite(amount)) input.placeholder = (amount * rate).toFixed(2);
    }

    const label = input.parentElement?.querySelector('.price-facet__label');
    if (label) label.textContent = '$';
  }

  function convertFilterInputs(root) {
    if (!(root instanceof Element || root instanceof Document)) return;

    if (root instanceof HTMLInputElement && root.matches('.price-facet__input')) convertFilterInput(root);
    root.querySelectorAll('.price-facet__input').forEach(convertFilterInput);
  }

  function convertRoot(root) {
    convertText(root);
    convertFilterInputs(root);
  }

  function scheduleConversion(root) {
    scheduledRoots.add(root);
    if (conversionPending) return;

    conversionPending = true;
    requestAnimationFrame(() => {
      conversionPending = false;
      const nextRoots = Array.from(scheduledRoots);
      scheduledRoots.clear();
      nextRoots.forEach(convertRoot);
    });
  }

  function observePrices() {
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === 'characterData') {
          scheduleConversion(record.target);
          continue;
        }

        for (const node of record.addedNodes) scheduleConversion(node);
      }
    });

    observer.observe(document.body, { childList: true, characterData: true, subtree: true });
  }

  function preparePriceFilters() {
    document.addEventListener(
      'change',
      (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.dataset.fitroomUsd !== 'true' || !input.value) return;

        const usdAmount = parseAmount(input.value);
        if (!Number.isFinite(usdAmount)) return;

        input.value = (usdAmount / rate).toFixed(2);
        setTimeout(() => {
          if (!input.isConnected || !input.value) return;
          const gelAmount = parseAmount(input.value);
          if (Number.isFinite(gelAmount)) input.value = (gelAmount * rate).toFixed(2);
        });
      },
      true
    );
  }

  function readCachedRate() {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (cached && Number.isFinite(cached.rate) && Date.now() - cached.savedAt < CACHE_TTL) return cached.rate;
    } catch (_error) {
      // Storage can be unavailable in privacy modes; fetching still works.
    }

    return 0;
  }

  async function fetchRate() {
    const response = await fetch(RATE_URL, { credentials: 'omit', mode: 'cors' });
    if (!response.ok) throw new Error(`Currency rate request failed: ${response.status}`);

    const payload = await response.json();
    const nextRate = Number(payload.rate);
    if (!Number.isFinite(nextRate) || nextRate <= 0) throw new Error('Currency rate response was invalid');

    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ rate: nextRate, savedAt: Date.now() }));
    } catch (_error) {
      // The live rate can still be used when storage is unavailable.
    }

    return nextRate;
  }

  async function initialize() {
    rate = readCachedRate();

    if (!rate) {
      try {
        rate = await fetchRate();
      } catch (_error) {
        return;
      }
    }

    convertRoot(document);
    preparePriceFilters();
    observePrices();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
